import { format, parseISO, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  type PaymentAgreement,
  getAgreementReasonLabel,
} from '@/hooks/finance/usePaymentAgreements';
import { CustomInstallmentsViewer } from './CustomInstallmentsViewer';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

interface NewProposalDetailsProps {
  agreement: PaymentAgreement;
}

export function NewProposalDetails({ agreement }: NewProposalDetailsProps) {
  const installmentAmount = agreement.num_installments > 0 
    ? agreement.agreed_amount / agreement.num_installments 
    : 0;
    
  // Calculate last payment date (only for uniform installments)
  const lastDueDate = agreement.first_due_date && !agreement.custom_installments
    ? format(
        addMonths(parseISO(agreement.first_due_date), agreement.num_installments - 1), 
        'dd/MM/yyyy', 
        { locale: ptBR }
      )
    : '-';

  const hasCustomInstallments = agreement.custom_installments && agreement.custom_installments.length > 0;
  
  return (
    <div className="border rounded-lg p-3 bg-primary/5 border-primary/20">
      <p className="text-xs font-medium uppercase text-primary mb-2">
        NOVA PROPOSTA
      </p>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Saldo a renegociar:</span>{' '}
          <span className="font-medium">{formatCurrency(agreement.original_balance)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Nova estrutura:</span>{' '}
          {hasCustomInstallments ? (
            <span className="font-medium text-primary">
              {agreement.num_installments}x (valores customizados)
            </span>
          ) : (
            <span className="font-medium text-primary">
              {agreement.num_installments}x de {formatCurrency(installmentAmount)}
            </span>
          )}
        </div>
        {!hasCustomInstallments && agreement.first_due_date && (
          <>
            <div>
              <span className="text-muted-foreground">1ª parcela:</span>{' '}
              <span className="font-medium">
                {format(parseISO(agreement.first_due_date), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Última parcela:</span>{' '}
              <span className="font-medium">{lastDueDate}</span>
            </div>
          </>
        )}
      </div>

      {/* Custom Installments Table */}
      {hasCustomInstallments && (
        <CustomInstallmentsViewer 
          installments={agreement.custom_installments!} 
          currency="USD" 
        />
      )}
      
      {/* Reason and Justification */}
      <div className="mt-3 pt-3 border-t border-primary/20 space-y-2">
        {agreement.reason && (
          <div className="text-sm">
            <span className="text-muted-foreground">Motivo:</span>{' '}
            <span className="font-medium">{getAgreementReasonLabel(agreement.reason)}</span>
          </div>
        )}
        {agreement.reason_notes && (
          <div className="text-sm">
            <span className="text-muted-foreground">Justificativa:</span>
            <p className="mt-1 p-2 bg-background rounded text-sm">
              {agreement.reason_notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
